import { FC, useMemo } from "react";
import { Button } from "@radix-ui/themes";
import { ColumnDef } from "@tanstack/react-table";
import { Table } from "@/components/table/table";
import { SyncClientGroupSummarySchema } from "@/domains/openapi/types";
import { orderBy } from "lodash-es";
import { DateTime } from "luxon";
import { UuidFieldRow } from "@/components/table/uuid-field-row";
import { DateTimeFieldRow } from "@/components/table/datetime-field-row";
import { NumberFieldRow } from "@/components/table/number-field-row";
import { TextFieldRow } from "@/components/table/text-field-row";

interface SyncClientGroupsTableProps {
  data: SyncClientGroupSummarySchema[];
  onViewSyncClientGroup: (id: string) => void;
  onViewSyncCvr: (id: string) => void;
  pageSize: number;
}

export const SyncClientGroupsTable: FC<SyncClientGroupsTableProps> = ({
  data,
  onViewSyncClientGroup,
  onViewSyncCvr,
  pageSize,
}) => {
  const columns = useMemo<ColumnDef<SyncClientGroupSummarySchema>[]>(
    () => [
      {
        header: "Client Group ID",
        cell: ({ row }) => <UuidFieldRow id={row.original.id} />,
      },
      {
        header: "View Group",
        cell: ({ row }) => (
          <Button variant="outline" size="1" onClick={() => onViewSyncClientGroup(row.original.id)}>
            View Group
          </Button>
        ),
      },
      {
        header: "Platform",
        cell: ({ row }) => <TextFieldRow value={row.original.platform_kind} />,
      },
      {
        header: "Last Sync Query",
        cell: ({ row }) => <DateTimeFieldRow dateStr={row.original.last_queried_at} />,
      },
      {
        header: "CVR ID",
        cell: ({ row }) => <UuidFieldRow id={row.original.sync_cvr?.id ?? ""} />,
      },
      {
        header: "CVR Version",
        cell: ({ row }) => <NumberFieldRow value={row.original.sync_cvr?.latest_cvr_version} />,
      },

      {
        header: "View CVR",
        cell: ({ row }) => (
          <Button
            variant="outline"
            size="1"
            disabled={!row.original.sync_cvr}
            onClick={() => row.original.sync_cvr && onViewSyncCvr(row.original.sync_cvr.id)}
          >
            View CVR
          </Button>
        ),
      },
    ],
    [onViewSyncClientGroup]
  );

  const orderedData = useMemo(
    () =>
      orderBy(
        data,
        x => (x.last_queried_at ? DateTime.fromISO(x.last_queried_at).toMillis() : -Infinity),
        "desc"
      ),
    [data]
  );
  return <Table data={orderedData} columns={columns} pageSize={pageSize} />;
};
